import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAdminApi } from '@damar/admin-hooks';
import { mapEmployee } from '@/features/hr/shared/api';
import type {
  MissionOrder,
  InterimEmployee,
  ClientCompany,
  Contact,
  CreateMissionPayload,
  AssignWorkerPayload,
  CreateContactPayload,
  MissionMargin,
  PeopleAnalyticsSummary,
  ComplianceDocumentAlert,
  CarenceViolation,
  ExitInterview,
  CreateExitInterviewPayload,
  UpdateExitInterviewPayload,
  InterimCandidate,
  OnboardingChecklist,
  OnboardingChecklistItem,
  MissionFeedback,
  FeedbackDirection,
  AnnualReview,
  SmartObjective,
  TrainingCatalogEntry,
  TrainingRequest,
  TrainingBudget,
  TrainingRequestStatus,
  SkillCatalogEntry,
  EmployeeSkill,
  SkillGap,
} from './types';

// === Mappers ===

export function mapMissionOrder(raw: any): MissionOrder {
  return {
    ...raw,
    // Public UUID — drives /hr/missions/{uuid} routes (IDOR mitigation).
    id: raw.publicId ?? raw.id ?? '',
    // Transitional BIGINT — internal FK joins (timesheets, billing).
    missionOrderKey: raw.missionOrderKey ?? { id: raw.orderId },
    status: raw.status ?? raw.statusValue ?? 'DRAFT',
    contractType: raw.contractType ?? raw.contractTypeValue ?? '',
    validationMode: raw.validationMode ?? raw.validationModeValue ?? null,
    // New enriched fields from projection
    clientName: raw.clientName ?? null,
    assignedEmployeeName: raw.assignedEmployeeName ?? null,
  };
}

export function mapClientCompany(raw: any): ClientCompany {
  return {
    ...raw,
    companyId: raw.companyId ?? raw.id,
    companyName: raw.companyName ?? raw.name ?? '',
    companyCode: raw.companyCode ?? raw.code ?? '',
    isActive: raw.isActive ?? raw.active ?? true,
  };
}

export const hrAdminKeys = {
  missions: (params?: Record<string, unknown>) => ['hr', 'admin', 'missions', params] as const,
  mission: (id: string | number) => ['hr', 'admin', 'missions', id] as const,
  missionsEndingSoon: (days: number) => ['hr', 'admin', 'missions', 'ending-soon', days] as const,
  missionMargins: () => ['hr', 'admin', 'missions', 'margins'] as const,
  clients: (params?: Record<string, unknown>) => ['hr', 'admin', 'clients', params] as const,
  clientContacts: (companyId: number) => ['hr', 'admin', 'clients', companyId, 'contacts'] as const,
  clientMissions: (clientId: number) => ['hr', 'admin', 'clients', clientId, 'missions'] as const,
  interimaires: (params?: Record<string, unknown>) => ['hr', 'admin', 'interimaires', params] as const,
  interContrat: () => ['hr', 'admin', 'inter-contrat'] as const,
};

// === Missions ===

export function useMissions(params?: { status?: string }) {
  return useQuery({
    queryKey: hrAdminKeys.missions(params),
    queryFn: async () => {
      const { data } = await getAdminApi().get<MissionOrder[]>('/api/v1/hr/missions', { params });
      return (data as any[]).map(mapMissionOrder);
    },
  });
}

export function useMission(id: string | undefined) {
  return useQuery({
    queryKey: hrAdminKeys.mission(id ?? ''),
    queryFn: async () => {
      const { data } = await getAdminApi().get<MissionOrder>(`/api/v1/hr/missions/${id}`);
      return mapMissionOrder(data);
    },
    enabled: !!id,
  });
}

export function useMissionsEndingSoon(warningDays = 15) {
  return useQuery({
    queryKey: hrAdminKeys.missionsEndingSoon(warningDays),
    queryFn: async () => {
      const { data } = await getAdminApi().get<MissionOrder[]>(
        '/api/v1/hr/missions/ending-soon',
        { params: { warningDays } },
      );
      return (data as any[]).map(mapMissionOrder);
    },
  });
}

export function useMissionMargins() {
  return useQuery({
    queryKey: hrAdminKeys.missionMargins(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<MissionMargin[]>('/api/v1/hr/missions/margins');
      return data;
    },
  });
}

export function usePeopleAnalytics() {
  return useQuery({
    queryKey: ['hr', 'admin', 'analytics', 'people'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<PeopleAnalyticsSummary>('/api/v1/hr/analytics/people');
      return data;
    },
  });
}

export function useComplianceDocumentAlerts() {
  return useQuery({
    queryKey: ['hr', 'admin', 'compliance', 'documents-alerts'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<ComplianceDocumentAlert[]>('/api/v1/hr/compliance/documents-alerts');
      return data;
    },
  });
}

export function useCarenceViolations() {
  return useQuery({
    queryKey: ['hr', 'admin', 'compliance', 'carence'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<CarenceViolation[]>('/api/v1/hr/compliance/carence');
      return data;
    },
  });
}

// === Exit Interviews (B5) ===

export function useExitInterviews() {
  return useQuery({
    queryKey: ['hr', 'admin', 'exit-interviews'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<ExitInterview[]>('/api/v1/hr/exit-interviews');
      return data;
    },
  });
}

export function useExitInterview(id: number | undefined) {
  return useQuery({
    queryKey: ['hr', 'admin', 'exit-interviews', id ?? 0] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<ExitInterview>(`/api/v1/hr/exit-interviews/${id}`);
      return data;
    },
    enabled: !!id && id > 0,
  });
}

export function useCreateExitInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateExitInterviewPayload) => {
      const { data } = await getAdminApi().post<ExitInterview>('/api/v1/hr/exit-interviews', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'exit-interviews'] });
    },
  });
}

// === Onboarding (B1) ===

export function useOnboardings() {
  return useQuery({
    queryKey: ['hr', 'admin', 'onboardings'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<OnboardingChecklist[]>('/api/v1/hr/onboarding');
      return data;
    },
  });
}

export function useOnboarding(id: number | undefined) {
  return useQuery({
    queryKey: ['hr', 'admin', 'onboardings', id ?? 0] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<OnboardingChecklist>(`/api/v1/hr/onboarding/${id}`);
      return data;
    },
    enabled: !!id && id > 0,
  });
}

export function useCreateOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { employeeId: number; startDate: string; managerId?: number; createdBy: string }) => {
      const { data } = await getAdminApi().post<OnboardingChecklist>('/api/v1/hr/onboarding', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'onboardings'] });
    },
  });
}

export function useUpdateOnboardingMilestone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, milestone, done, notes }: { id: number; milestone: 'D1' | 'D30' | 'D60' | 'D90'; done: boolean; notes?: string }) => {
      const { data } = await getAdminApi().put<OnboardingChecklist>(`/api/v1/hr/onboarding/${id}/milestone`, { milestone, done, notes });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'onboardings'] });
    },
  });
}

export function useUpdateOnboardingItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, items }: { id: number; items: OnboardingChecklistItem[] }) => {
      const { data } = await getAdminApi().put<OnboardingChecklist>(`/api/v1/hr/onboarding/${id}/items`, { items });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'onboardings'] });
    },
  });
}

// === Skills / GPEC (B6) ===

export function useSkillsCatalog() {
  return useQuery({
    queryKey: ['hr', 'admin', 'skills', 'catalog'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<SkillCatalogEntry[]>('/api/v1/hr/skills/catalog');
      return data;
    },
  });
}

export function useUpsertSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<SkillCatalogEntry>) => {
      const { data } = await getAdminApi().post<SkillCatalogEntry>('/api/v1/hr/skills/catalog', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'skills'] });
    },
  });
}

export function useAllEmployeeSkills() {
  return useQuery({
    queryKey: ['hr', 'admin', 'skills', 'employees'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<EmployeeSkill[]>('/api/v1/hr/skills/employees');
      return data;
    },
  });
}

export function useEmployeeSkills(employeeId: number | undefined) {
  return useQuery({
    queryKey: ['hr', 'admin', 'skills', 'employees', employeeId ?? 0] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<EmployeeSkill[]>(`/api/v1/hr/skills/employees/${employeeId}`);
      return data;
    },
    enabled: !!employeeId && employeeId > 0,
  });
}

export function useUpsertEmployeeSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      employeeId: number;
      skillId: number;
      currentLevel?: number;
      targetLevel?: number;
      assessedBy?: string;
      selfAssessed?: boolean;
      notes?: string;
    }) => {
      const { data } = await getAdminApi().post<EmployeeSkill>('/api/v1/hr/skills/employees', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'skills'] });
    },
  });
}

export function useSkillGaps() {
  return useQuery({
    queryKey: ['hr', 'admin', 'skills', 'gaps'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<SkillGap[]>('/api/v1/hr/skills/gaps');
      return data;
    },
  });
}

export function useUpsertPositionSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { positionId: number; skillId: number; requiredLevel: number; isMandatory: boolean }) => {
      const { data } = await getAdminApi().post('/api/v1/hr/skills/positions', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'skills'] });
    },
  });
}

// === Trainings (B4) ===

export function useTrainingCatalog() {
  return useQuery({
    queryKey: ['hr', 'admin', 'trainings', 'catalog'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<TrainingCatalogEntry[]>('/api/v1/hr/trainings/catalog');
      return data;
    },
  });
}

export function useUpsertTrainingCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<TrainingCatalogEntry> & { trainingId?: number; createdBy?: string }) => {
      const { data } = await getAdminApi().post<TrainingCatalogEntry>('/api/v1/hr/trainings/catalog', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'trainings'] });
    },
  });
}

export function useTrainingRequests() {
  return useQuery({
    queryKey: ['hr', 'admin', 'trainings', 'requests'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<TrainingRequest[]>('/api/v1/hr/trainings/requests');
      return data;
    },
  });
}

export function useCreateTrainingRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      employeeId: number;
      trainingId?: number;
      customTitle?: string;
      customProvider?: string;
      customCost?: number;
      motive?: string;
      preferredDate?: string;
      createdBy?: string;
    }) => {
      const { data } = await getAdminApi().post<TrainingRequest>('/api/v1/hr/trainings/requests', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'trainings'] });
    },
  });
}

export function useUpdateTrainingRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: {
      status?: TrainingRequestStatus;
      approvedCost?: number;
      managerComment?: string;
      hrComment?: string;
      sessionStartDate?: string;
      sessionEndDate?: string;
      completionRating?: number;
      completionComment?: string;
    } }) => {
      const { data } = await getAdminApi().put<TrainingRequest>(`/api/v1/hr/trainings/requests/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'trainings'] });
    },
  });
}

export function useTrainingBudgets(year?: number) {
  return useQuery({
    queryKey: ['hr', 'admin', 'trainings', 'budgets', year] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<TrainingBudget[]>('/api/v1/hr/trainings/budgets', { params: year ? { year } : {} });
      return data;
    },
  });
}

export function useUpsertTrainingBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { year: number; employeeId?: number; departmentId?: number; allocatedAmount: number; notes?: string }) => {
      const { data } = await getAdminApi().post<TrainingBudget>('/api/v1/hr/trainings/budgets', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'trainings'] });
    },
  });
}

// === Annual Reviews (B2) ===

export function useAnnualReviews() {
  return useQuery({
    queryKey: ['hr', 'admin', 'annual-reviews'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<AnnualReview[]>('/api/v1/hr/annual-reviews');
      return data;
    },
  });
}

export function useAnnualReview(id: number | undefined) {
  return useQuery({
    queryKey: ['hr', 'admin', 'annual-reviews', id ?? 0] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<AnnualReview>(`/api/v1/hr/annual-reviews/${id}`);
      return data;
    },
    enabled: !!id && id > 0,
  });
}

export function useCreateAnnualReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { employeeId: number; reviewerId?: number; reviewYear?: number; createdBy?: string }) => {
      const { data } = await getAdminApi().post<AnnualReview>('/api/v1/hr/annual-reviews', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'annual-reviews'] });
    },
  });
}

export function useUpdateAnnualReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: {
      objectives?: SmartObjective[];
      careerWish?: string;
      trainingNeeds?: string;
      mobilityWish?: string;
      selfOverallRating?: number;
      managerOverallRating?: number;
      selfComment?: string;
      managerComment?: string;
      status?: string;
      conductedDate?: string;
    }}) => {
      const { data } = await getAdminApi().put<AnnualReview>(`/api/v1/hr/annual-reviews/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'annual-reviews'] });
    },
  });
}

export function useSignAnnualReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, signerType }: { id: number; signerType: 'EMPLOYEE' | 'MANAGER' }) => {
      const { data } = await getAdminApi().post<AnnualReview>(`/api/v1/hr/annual-reviews/${id}/sign`, { signerType });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'annual-reviews'] });
    },
  });
}

// === Mission Feedbacks (A6) ===

export function useMissionFeedbacks() {
  return useQuery({
    queryKey: ['hr', 'admin', 'mission-feedbacks'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<MissionFeedback[]>('/api/v1/hr/mission-feedbacks');
      return data;
    },
  });
}

export function useMissionFeedbacksByEmployee(employeeId: number | undefined) {
  return useQuery({
    queryKey: ['hr', 'admin', 'mission-feedbacks', 'employee', employeeId ?? 0] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<MissionFeedback[]>(`/api/v1/hr/mission-feedbacks/employee/${employeeId}`);
      return data;
    },
    enabled: !!employeeId && employeeId > 0,
  });
}

export function useCreateMissionFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      missionId: number;
      direction: FeedbackDirection;
      ratingOverall?: number;
      ratingSkills?: number;
      ratingAttitude?: number;
      ratingReliability?: number;
      nps?: number;
      strengths?: string;
      improvements?: string;
      comment?: string;
      wouldWorkAgain?: boolean;
      submittedBy?: string;
      submittedByType?: string;
    }) => {
      const { data } = await getAdminApi().post<MissionFeedback>('/api/v1/hr/mission-feedbacks', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'mission-feedbacks'] });
    },
  });
}

export function useValidateOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, confirmed, comment, validatedBy }: { id: number; confirmed: boolean; comment?: string; validatedBy: string }) => {
      const { data } = await getAdminApi().put<OnboardingChecklist>(`/api/v1/hr/onboarding/${id}/validate`, { confirmed, comment, validatedBy });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'onboardings'] });
    },
  });
}

export function useUpdateExitInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: UpdateExitInterviewPayload }) => {
      const { data } = await getAdminApi().put<ExitInterview>(`/api/v1/hr/exit-interviews/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'exit-interviews'] });
    },
  });
}

export function useCreateMission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateMissionPayload) => {
      const { data } = await getAdminApi().post<MissionOrder>('/api/v1/hr/missions', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'missions'] });
    },
  });
}

export function useUpdateMission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<CreateMissionPayload> }) => {
      const { data } = await getAdminApi().put<MissionOrder>(`/api/v1/hr/missions/${id}`, { ...payload, orderId: id });
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'missions'] });
      queryClient.invalidateQueries({ queryKey: hrAdminKeys.mission(variables.id) });
    },
  });
}

export function useAssignWorker() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ missionId, payload }: { missionId: string; payload: AssignWorkerPayload }) => {
      const { data } = await getAdminApi().post(`/api/v1/hr/missions/${missionId}/assign`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'missions'] });
      queryClient.invalidateQueries({ queryKey: ['hr', 'admin', 'interimaires'] });
    },
  });
}

// === Clients ===

export function useClients() {
  return useQuery({
    queryKey: hrAdminKeys.clients(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<ClientCompany[]>('/api/v1/companies', {
        params: { type: 'CLIENT' },
      });
      return (data as any[]).map(mapClientCompany);
    },
  });
}

export function useClientContacts(companyId: number | undefined) {
  return useQuery({
    queryKey: hrAdminKeys.clientContacts(companyId ?? 0),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Contact[]>('/api/v1/contacts', {
        params: { companyId },
      });
      return data;
    },
    enabled: !!companyId && companyId > 0,
  });
}

export function useClientMissions(clientId: number | undefined) {
  return useQuery({
    queryKey: hrAdminKeys.clientMissions(clientId ?? 0),
    queryFn: async () => {
      const { data } = await getAdminApi().get<MissionOrder[]>(
        `/api/v1/hr/missions/client/${clientId}`,
      );
      return (data as any[]).map(mapMissionOrder);
    },
    enabled: !!clientId && clientId > 0,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateContactPayload) => {
      const { data } = await getAdminApi().post<Contact>('/api/v1/contacts', payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrAdminKeys.clientContacts(variables.companyId) });
    },
  });
}

// === Interimaires ===

function mapInterimEmployee(raw: any): InterimEmployee {
  return {
    ...raw,
    employeeKey: raw.employeeKey ?? { id: raw.employeeId },
    employmentStatus: raw.employmentStatus ?? raw.employmentStatusValue ?? 'ACTIVE',
  };
}

export function useInterimaires() {
  return useQuery({
    queryKey: hrAdminKeys.interimaires(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<InterimEmployee[]>('/api/v1/hr/employees/interimaires');
      return (data as any[]).map(mapInterimEmployee);
    },
  });
}

// TODO(handoff): expose /api/v1/hr/interim/employees/:id on backend for direct fetch.
// For now we reuse the list endpoint and filter client-side so the detail page has data.
export function useInterimaire(id: number | undefined) {
  return useQuery({
    queryKey: ['hr', 'admin', 'interimaires', 'detail', id ?? 0] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<InterimEmployee[]>('/api/v1/hr/employees/interimaires');
      const list = (data as any[]).map(mapInterimEmployee);
      return list.find((e) => e.employeeKey.id === id) ?? null;
    },
    enabled: !!id && id > 0,
  });
}

export function useInterimairesForClient(clientCompanyId: number | undefined) {
  return useQuery({
    queryKey: [...hrAdminKeys.interimaires(), { clientCompanyId }] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<InterimEmployee[]>(
        '/api/v1/hr/employees/interimaires',
        { params: { clientCompanyId } },
      );
      return (data as any[]).map(mapInterimEmployee);
    },
    enabled: !!clientCompanyId && clientCompanyId > 0,
  });
}

export function useSearchInterimCandidates(params: {
  skills?: string[];
  zone?: string;
  availability?: string;
}) {
  return useQuery({
    queryKey: ['hr', 'admin', 'interimaires', 'search', params] as const,
    queryFn: async () => {
      const search = new URLSearchParams();
      (params.skills ?? []).forEach(s => search.append('skill', s));
      if (params.zone) search.set('zone', params.zone);
      if (params.availability) search.set('availability', params.availability);
      const { data } = await getAdminApi().get<InterimCandidate[]>(
        `/api/v1/hr/employees/interimaires/search?${search.toString()}`
      );
      return data;
    },
  });
}

export function useAvailableInterimaires() {
  return useQuery({
    queryKey: ['hr', 'admin', 'interimaires', 'available'] as const,
    queryFn: async () => {
      const { data } = await getAdminApi().get<InterimEmployee[]>('/api/v1/hr/employees/interimaires/available');
      return (data as any[]).map(mapInterimEmployee);
    },
  });
}

export function useInterContrat() {
  return useQuery({
    queryKey: hrAdminKeys.interContrat(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<InterimEmployee[]>('/api/v1/hr/employees/inter-contrat');
      return (data as unknown[]).map(mapEmployee) as unknown as InterimEmployee[];
    },
  });
}

export function useSendNotification() {
  return useMutation({
    mutationFn: async (payload: {
      templateCode: string;
      channelType: string;
      recipientPhone?: string;
      recipientEmail?: string;
      variables: Record<string, string>;
    }) => {
      const { data } = await getAdminApi().post('/api/v1/communication/transactional/send', payload);
      return data;
    },
  });
}
